import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { avatarColor, initial, guestName } from '../utils/format';
import './Navbar.css';

const applyTheme = (dark) => {
  document.documentElement.classList.toggle('dark-mode', dark);
  document.body.classList.toggle('dark-mode', dark);
};

const Navbar = () => {
  const location = useLocation();
  const { token, user, isGuest, guestId, logout, fetchUserData } = useAuth();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    applyTheme(darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    const next = !document.documentElement.classList.contains('dark-mode');
    localStorage.setItem('darkMode', next);
    setDarkMode(next);
  };

  // Keep the rating in the header current after games (skipped while in a game room).
  const path = location.pathname;
  const inGame = path.startsWith('/game/');
  const lastPath = useRef(path);
  useEffect(() => {
    if (lastPath.current === path) return;
    lastPath.current = path;
    if (token && !inGame) fetchUserData();
  }, [path, inGame, token, fetchUserData]);

  const section = path === '/' || inGame ? 'home' : path.startsWith('/daily') ? 'daily' : path.startsWith('/leaderboard') ? 'leaderboard' : null;
  const links = [
    { key: 'home', to: '/', label: 'Home' },
    { key: 'daily', to: '/daily', label: 'Daily' },
    { key: 'leaderboard', to: '/leaderboard', label: 'Leaderboard' }
  ];

  const loggedIn = !!token;
  const guest = !loggedIn && isGuest;
  const [guestLabel, setGuestLabel] = useState('Guest');
  useEffect(() => {
    let live = true;
    guestName(guestId).then((name) => { if (live) setGuestLabel(name); }).catch(() => {});
    return () => { live = false; };
  }, [guestId]);
  const showLogin = !loggedIn && path !== '/login';
  const hasChip = (loggedIn && user) || guest;

  return (
    <header className="nav">
      <div className={`nav-in${hasChip ? ' has-chip' : ''}`}>
        <Link to="/" className="nav-brand">
          <span className="nav-glyph" aria-hidden="true"><i /><i /><i /><i /></span>
          <span>Connections Racer</span>
        </Link>

        <nav className="nav-links" aria-label="Main">
          {links.map((l) => (
            <Link key={l.key} to={l.to} className="nav-link" aria-current={section === l.key ? 'page' : undefined}>
              {l.label}
            </Link>
          ))}
        </nav>

        <button
          type="button"
          className="nav-icon"
          onClick={toggleDarkMode}
          aria-label="Switch light or dark mode"
          title="Switch light or dark mode"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 1.75a6.25 6.25 0 0 1 0 12.5z" fill="currentColor" />
          </svg>
        </button>

        {loggedIn && user && (
          <Link to={`/profile/${user.username}`} className="nav-user" aria-label={`Your profile, ${user.username}`}>
            <span className="cr-av" style={{ background: avatarColor(user.username) }}>{initial(user.username)}</span>
            <b>{user.username}</b>
            <span className="nav-rating cr-tnum">{user.rating ?? 1000}</span>
          </Link>
        )}

        {guest && (
          <span className="nav-user">
            <span className="cr-av" style={{ background: avatarColor(guestLabel) }}>{initial(guestLabel)}</span>
            <b>{guestLabel}</b>
          </span>
        )}

        <div className="nav-auth">
          {loggedIn ? (
            <button type="button" className="cr-btn sm" onClick={logout}>Logout</button>
          ) : showLogin ? (
            <Link to="/login" className="cr-btn sm solid">Login</Link>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
